import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Forward, Paperclip, RefreshCw, GitBranch, Trash2 } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { useAuthStore } from '@/lib/auth-store'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { InboxSummaryCard } from '@/components/InboxSummaryCard'
import {
  DEPARTMENTS,
  DEPARTMENT_BY_VALUE,
  type Department,
} from '@/lib/departments'
import {
  classificationListPreview,
  urgencyBadgeClass,
} from '@/lib/inbox-classification'
import { cn } from '@/lib/utils'

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')

type Status =
  | 'pending'
  | 'classified'
  | 'routed'
  | 'converted'
  | 'forwarded'
  | 'archived'
  | 'spam'
  | 'failed'

interface RuleFlowStep {
  ruleName: string
  action: string
  actionTarget: string | null
  matchedAt: string
}

interface Attachment {
  id: string
  filename: string
  contentType: string | null
  sizeBytes: number | null
}

interface TeamMember {
  id: string
  name: string
  email: string
  role: string
  active: boolean
}

interface InboundEmail {
  id: string
  source: string
  sourceMailbox: string | null
  fromEmail: string
  fromName: string | null
  toAddress: string
  subject: string
  textBody: string | null
  receivedAt: string
  readAt: string | null
  status: Status
  classification: Record<string, unknown> | null
  routingDecision: Record<string, unknown> | null
  routedToRole: string | null
  routedToMember: { id: string; name: string; email: string; role: string } | null
  assignedDepartment: Department | null
  assignedToUserId: string | null
  inquiryId: string | null
  error: string | null
  attachments?: Attachment[]
  ruleFlow?: string
  ruleFlowSteps?: RuleFlowStep[]
  lastAction?: string
  lastActionAt?: string
}

interface InboxResponse {
  data?: InboundEmail[]
  items?: InboundEmail[]
}

const STATUS_COLOUR: Record<Status, string> = {
  pending: 'bg-slate-100 text-slate-700',
  classified: 'bg-blue-100 text-blue-700',
  routed: 'bg-indigo-100 text-indigo-700',
  converted: 'bg-emerald-100 text-emerald-700',
  forwarded: 'bg-teal-100 text-teal-700',
  archived: 'bg-slate-100 text-slate-500',
  spam: 'bg-rose-100 text-rose-700',
  failed: 'bg-red-100 text-red-700',
}

function formatListTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Turn stored Zod/JSON errors into a short summary + wrapped detail for the UI. */
function formatInboxError(raw: string): { summary: string; detail: string } {
  const trimmed = raw.trim()
  if (!trimmed) {
    return { summary: 'Classification failed', detail: '' }
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
      const lines = parsed
        .map((issue) => {
          if (!issue || typeof issue !== 'object') return null
          const path = Array.isArray((issue as { path?: unknown }).path)
            ? (issue as { path: unknown[] }).path.join('.')
            : 'field'
          const message =
            typeof (issue as { message?: unknown }).message === 'string'
              ? (issue as { message: string }).message
              : 'invalid'
          return `${path}: ${message}`
        })
        .filter(Boolean)
      return {
        summary: `Classification failed (${parsed.length} validation issue${parsed.length === 1 ? '' : 's'})`,
        detail: lines.join('\n'),
      }
    }
  } catch {
    // plain text error — show as-is
  }
  if (trimmed.length > 120) {
    return { summary: 'Classification failed', detail: trimmed }
  }
  return { summary: trimmed, detail: '' }
}

function inboxErrorPreview(raw: string): string {
  return formatInboxError(raw).summary
}

export function InboxPage() {
  const qc = useQueryClient()
  const artistId = useAuthStore((s) => s.user?.artistId)
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [forwardTo, setForwardTo] = useState('')
  const [forwardBusy, setForwardBusy] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [showRawDetails, setShowRawDetails] = useState(false)

  const { data, error, isLoading } = useQuery<InboundEmail[], ApiClientError>({
    queryKey: ['inbox', statusFilter],
    queryFn: async () => {
      const q = statusFilter === 'all' ? '' : `?status=${statusFilter}`
      const res = await artistFetch<InboundEmail[] | InboxResponse>(
        `/inbound${q}`,
      )
      if (Array.isArray(res)) return res
      return res.items ?? (res.data ?? [])
    },
  })

  const { data: team = [] } = useQuery<TeamMember[], ApiClientError>({
    queryKey: ['team'],
    queryFn: async () => {
      const res = await artistFetch<TeamMember[] | { data: TeamMember[] }>('/team')
      if (Array.isArray(res)) return res
      return res.data ?? []
    },
  })

  const selected = data?.find((e) => e.id === selectedId) ?? null
  const assignedMember = selected?.assignedToUserId
    ? team.find((m) => m.id === selected.assignedToUserId)
    : null

  async function reprocess(id: string) {
    try {
      await artistFetch(`/inbound/${id}/reprocess`, { method: 'POST' })
      qc.invalidateQueries({ queryKey: ['inbox'] })
      qc.invalidateQueries({ queryKey: ['rule-log', id] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    }
  }

  async function forwardEmail(id: string) {
    const to = forwardTo.trim()
    if (!to) {
      alert('Enter an email address to forward to.')
      return
    }
    setForwardBusy(true)
    try {
      await artistFetch(`/inbound/${id}/forward`, {
        method: 'POST',
        body: { to },
      })
      setForwardTo('')
      qc.invalidateQueries({ queryKey: ['inbox'] })
      qc.invalidateQueries({ queryKey: ['rule-log', id] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Forward failed.')
    } finally {
      setForwardBusy(false)
    }
  }

  async function deleteEmail(id: string) {
    if (
      !window.confirm(
        'Remove this email from the inbox? It will be archived and can still be found under the Archived filter.',
      )
    ) {
      return
    }
    setDeleteBusy(true)
    try {
      await artistFetch(`/inbound/${id}`, { method: 'DELETE' })
      if (selectedId === id) setSelectedId(null)
      qc.invalidateQueries({ queryKey: ['inbox'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Delete failed.')
    } finally {
      setDeleteBusy(false)
    }
  }

  async function patchTriage(
    id: string,
    body: {
      readAt?: string | null
      assignedDepartment?: Department | null
      assignedToUserId?: string | null
    },
  ) {
    try {
      await artistFetch(`/inbound/${id}`, { method: 'PATCH', body })
      qc.invalidateQueries({ queryKey: ['inbox'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Update failed.')
    }
  }

  async function assignDepartment(id: string, dept: Department | null) {
    await patchTriage(id, { assignedDepartment: dept })
  }

  async function assignMember(id: string, memberId: string | null) {
    await patchTriage(id, { assignedToUserId: memberId })
  }

  async function markRead(id: string, asRead: boolean) {
    await patchTriage(id, { readAt: asRead ? new Date().toISOString() : null })
  }

  return (
    <div>
      <PageHeader
        title="Inbox"
        subtitle="Your operational centre for incoming correspondence. AI analyses every message before routing it to the right person."
      />

      <div className="mb-4 flex items-center gap-2">
        <label className="text-xs uppercase tracking-wider text-ovation-muted">
          Filter
        </label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as Status | 'all')}
          className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="classified">Classified</option>
          <option value="routed">Routed</option>
          <option value="converted">Converted</option>
          <option value="forwarded">Forwarded</option>
          <option value="archived">Archived</option>
          <option value="spam">Spam</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <AsyncBoundary isLoading={isLoading} error={error}>
        {(data ?? []).length === 0 ? (
          <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
            No emails in the inbox yet. Post to{' '}
            <code className="text-xs">/webhooks/inbound-email</code> or submit
            the public form at{' '}
            <code className="text-xs">/public/inquiries/{'{artistSlug}'}</code>{' '}
            to see them appear here.
          </div>
        ) : (
          <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
            {/* List */}
            <div className="min-w-0 space-y-2">
              {data?.map((email) => {
                const preview = classificationListPreview(email.classification)
                const deptLabel = email.assignedDepartment
                  ? (DEPARTMENT_BY_VALUE[email.assignedDepartment]?.label ??
                    email.assignedDepartment)
                  : null
                return (
                <button
                  type="button"
                  key={email.id}
                  onClick={() => setSelectedId(email.id)}
                  className={cn(
                    'w-full rounded-lg border bg-white p-3 text-left text-sm transition-colors',
                    selectedId === email.id
                      ? 'border-ovation-ink'
                      : 'border-ovation-ink/10 hover:border-ovation-ink/30',
                    !email.readAt && 'border-l-4 border-l-indigo-500',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 truncate font-medium">
                      {email.fromName ?? email.fromEmail}
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded px-1.5 py-0.5 text-[10px]',
                        STATUS_COLOUR[email.status],
                      )}
                    >
                      {email.status}
                    </span>
                  </div>
                  <div className="truncate text-ovation-muted">
                    {email.subject}
                  </div>
                  {preview.category && (
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className="rounded bg-ovation-ink/5 px-1.5 py-0.5 text-[10px] text-ovation-ink/80">
                        {preview.category}
                      </span>
                      {preview.urgency && (
                        <span
                          className={cn(
                            'rounded px-1.5 py-0.5 text-[10px] font-medium',
                            urgencyBadgeClass(preview.urgency),
                          )}
                        >
                          {preview.urgency === 'urgent' ? 'Critical' : preview.urgency}
                        </span>
                      )}
                      {deptLabel && (
                        <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] text-indigo-700">
                          {deptLabel}
                        </span>
                      )}
                    </div>
                  )}
                  {email.ruleFlow ? (
                    <div className="mt-1 truncate text-[11px] text-ovation-ink/70">
                      {email.ruleFlow}
                    </div>
                  ) : email.status === 'failed' && email.error ? (
                    <div className="mt-1 line-clamp-2 break-words text-[11px] text-red-600">
                      {inboxErrorPreview(email.error)}
                    </div>
                  ) : null}
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-ovation-muted">
                    <span>
                      {formatListTimestamp(
                        email.lastActionAt ?? email.receivedAt,
                      )}
                    </span>
                    {email.lastAction && (
                      <>
                        <span aria-hidden>·</span>
                        <span className="font-medium text-ovation-ink/80">
                          {email.lastAction}
                        </span>
                      </>
                    )}
                  </div>
                </button>
              )})}
            </div>

            {/* Detail */}
            <div className="min-w-0 overflow-hidden rounded-lg border border-ovation-ink/10 bg-white p-5">
              {selected ? (
                <div className="min-w-0 space-y-4 text-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-ovation-ink">
                        {selected.subject}
                      </div>
                      <div className="mt-1 text-ovation-muted">
                        {selected.fromName
                          ? `${selected.fromName} <${selected.fromEmail}>`
                          : selected.fromEmail}
                      </div>
                      <div className="text-xs text-ovation-muted">
                        To {selected.toAddress} ·{' '}
                        {formatListTimestamp(selected.receivedAt)}
                      </div>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded px-2 py-0.5 text-xs',
                        STATUS_COLOUR[selected.status],
                      )}
                    >
                      {selected.status}
                    </span>
                  </div>

                  <InboxSummaryCard
                    classification={selected.classification}
                    routingDecision={selected.routingDecision}
                    inquiryId={selected.inquiryId}
                    assignedDepartment={selected.assignedDepartment}
                    assignedName={assignedMember?.name ?? null}
                    routedToName={selected.routedToMember?.name ?? null}
                    attachments={selected.attachments ?? []}
                  />

                  <div className="flex flex-wrap items-end gap-3 rounded-lg border border-ovation-ink/10 bg-ovation-paper/20 p-3">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-ovation-muted">
                        Department
                      </label>
                      <select
                        value={selected.assignedDepartment ?? ''}
                        onChange={(e) =>
                          assignDepartment(
                            selected.id,
                            (e.target.value || null) as Department | null,
                          )
                        }
                        className="mt-1 rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
                      >
                        <option value="">— unassigned —</option>
                        {DEPARTMENTS.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-ovation-muted">
                        Assign to
                      </label>
                      <select
                        value={selected.assignedToUserId ?? ''}
                        onChange={(e) =>
                          assignMember(selected.id, e.target.value || null)
                        }
                        className="mt-1 max-w-[220px] rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
                      >
                        <option value="">— unassigned —</option>
                        {team
                          .filter((m) => m.active)
                          .map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name} ({m.role.replace(/_/g, ' ')})
                            </option>
                          ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => markRead(selected.id, !selected.readAt)}
                      className="inline-flex items-center gap-1 rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs hover:bg-white"
                    >
                      <Check className="h-3 w-3" />
                      {selected.readAt ? 'Mark unread' : 'Mark read'}
                    </button>
                  </div>

                  {selected.error && (() => {
                    const err = formatInboxError(selected.error)
                    return (
                      <div className="max-w-full rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                        <div className="break-words font-medium">{err.summary}</div>
                        {err.detail ? (
                          <pre className="mt-2 max-w-full whitespace-pre-wrap break-words font-sans">
                            {err.detail}
                          </pre>
                        ) : null}
                      </div>
                    )
                  })()}

                  <RuleTrail
                    emailId={selected.id}
                    {...(selected.ruleFlow ? { ruleFlow: selected.ruleFlow } : {})}
                    {...(selected.ruleFlowSteps
                      ? { ruleFlowSteps: selected.ruleFlowSteps }
                      : {})}
                  />

                  <div>
                    <div className="text-xs uppercase tracking-wider text-ovation-muted">
                      Message
                    </div>
                    <pre className="mt-1 max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-ovation-paper/40 p-3 font-sans text-xs">
                      {selected.textBody ?? '(no text body)'}
                    </pre>
                  </div>

                  {(selected.attachments?.length ?? 0) > 0 && artistId && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-ovation-muted">
                        Attachments
                      </div>
                      <ul className="mt-1 space-y-1">
                        {selected.attachments!.map((a) => (
                          <li key={a.id}>
                            <a
                              href={`${API_BASE_URL}/artists/${artistId}/inbound/${selected.id}/attachments/${a.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 text-xs text-blue-700 hover:underline"
                            >
                              <Paperclip className="h-3 w-3" />
                              {a.filename}
                              {a.sizeBytes != null && (
                                <span className="text-ovation-muted">
                                  ({Math.ceil(a.sizeBytes / 1024)} KB)
                                </span>
                              )}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(selected.classification || selected.routingDecision) && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowRawDetails((v) => !v)}
                        className="text-xs text-ovation-muted hover:text-ovation-ink hover:underline"
                      >
                        {showRawDetails ? 'Hide' : 'Show'} raw AI / routing data
                      </button>
                      {showRawDetails && (
                        <div className="mt-2 space-y-2">
                          {selected.classification && (
                            <pre className="max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-ovation-paper/40 p-3 text-xs">
                              {JSON.stringify(selected.classification, null, 2)}
                            </pre>
                          )}
                          {selected.routingDecision && (
                            <pre className="max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-ovation-paper/40 p-3 text-xs">
                              {JSON.stringify(selected.routingDecision, null, 2)}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-3 border-t border-ovation-ink/10 pt-3">
                    <div>
                      <div className="mb-1.5 text-xs uppercase tracking-wider text-ovation-muted">
                        Forward manually
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={forwardTo}
                          onChange={(e) => setForwardTo(e.target.value)}
                          placeholder="recipient@example.com"
                          className="min-w-0 flex-1 rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
                        />
                        <button
                          type="button"
                          disabled={forwardBusy}
                          onClick={() => forwardEmail(selected.id)}
                          className="flex shrink-0 items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-white hover:bg-ovation-ink/90 disabled:opacity-50"
                        >
                          <Forward className="h-3 w-3" />
                          {forwardBusy ? 'Sending…' : 'Forward'}
                        </button>
                      </div>
                      <p className="mt-1 text-[10px] text-ovation-muted">
                        Sends the message via Brevo with a link back to this
                        inbox.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => reprocess(selected.id)}
                        className="flex items-center gap-1 rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium text-ovation-ink hover:bg-ovation-ink/5"
                      >
                        <RefreshCw className="h-3 w-3" />
                        Reclassify
                      </button>
                      <button
                        type="button"
                        disabled={deleteBusy || selected.status === 'archived'}
                        onClick={() => deleteEmail(selected.id)}
                        className="flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-3 w-3" />
                        {deleteBusy ? 'Removing…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-ovation-muted">
                  Select an email on the left to see the AI summary, routing
                  decision, and full message.
                </div>
              )}
            </div>
          </div>
        )}
      </AsyncBoundary>
    </div>
  )
}

// ── Rule audit trail component ──────────────────────────────────────────────

interface RuleLogEntry {
  id: string
  artistId: string
  emailId: string
  threadId: string | null
  ruleId: string
  ruleName: string
  action: string
  actionTarget: string | null
  parentLogId: string | null
  matchedAt: string
}

function relativeTime(iso: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const diff = now - then
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function formatActionLabel(action: string, target?: string | null): string {
  const labels: Record<string, string> = {
    create_inquiry: 'Created inquiry',
    forward_to_role: 'Forwarded',
    forward_to_email: 'Forwarded',
    auto_reply: 'Auto-replied',
    archive: 'Archived',
    mark_spam: 'Marked spam',
    manual_forward: 'Forwarded (manual)',
    manual_archive: 'Removed from inbox',
  }
  const base =
    labels[action] ?? action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  if (target && action.includes('forward')) {
    return `${base} → ${target.replace(/_/g, ' ')}`
  }
  return base
}

function RuleTrail({
  emailId,
  ruleFlow,
  ruleFlowSteps,
}: {
  emailId: string
  ruleFlow?: string
  ruleFlowSteps?: RuleFlowStep[]
}) {
  const hasListSummary = Boolean(ruleFlowSteps && ruleFlowSteps.length > 0)
  const [expanded, setExpanded] = useState(hasListSummary)

  const { data, isLoading } = useQuery<RuleLogEntry[], ApiClientError>({
    queryKey: ['rule-log', emailId],
    queryFn: async () => {
      const res = await artistFetch<RuleLogEntry[] | { data: RuleLogEntry[] }>(
        `/inbound/${emailId}/rule-log`,
      )
      if (Array.isArray(res)) return res
      return res.data ?? []
    },
    enabled: expanded && !hasListSummary,
  })

  const entriesFromApi = data ?? []
  const entries: Array<{
    id: string
    ruleName: string
    action: string
    actionTarget: string | null
    parentLogId: string | null
    matchedAt: string
  }> = hasListSummary
    ? ruleFlowSteps!.map((s, i) => ({
        id: `list-${i}`,
        ruleName: s.ruleName,
        action: s.action,
        actionTarget: s.actionTarget,
        parentLogId: null,
        matchedAt: s.matchedAt,
      }))
    : entriesFromApi

  const depthMap = new Map<string, number>()
  for (const entry of entries) {
    if (!entry.parentLogId) {
      depthMap.set(entry.id, 0)
    } else {
      const parentDepth = depthMap.get(entry.parentLogId) ?? 0
      depthMap.set(entry.id, parentDepth + 1)
    }
  }

  return (
    <div>
      {ruleFlow && (
        <div className="mb-2 max-w-full break-words rounded-md bg-ovation-paper/50 px-3 py-2 text-xs text-ovation-ink/80">
          <span className="font-medium text-ovation-muted">Rules flow: </span>
          {ruleFlow}
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center gap-1.5 text-xs font-medium text-ovation-ink hover:underline"
      >
        <GitBranch className="h-3 w-3" />
        {expanded ? 'Hide' : 'Show'} rule trail
        {entries.length > 0 && (
          <span className="rounded-full bg-ovation-ink/10 px-1.5 py-0.5 text-[10px]">
            {entries.length}
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-2 space-y-1">
          {isLoading && !hasListSummary && (
            <div className="text-xs text-ovation-muted">Loading audit trail...</div>
          )}
          {!isLoading && entries.length === 0 && (
            <div className="text-xs text-ovation-muted">
              No rules matched this email.
            </div>
          )}
          {entries.map((entry) => {
            const depth = depthMap.get(entry.id) ?? 0
            return (
              <div
                key={entry.id}
                className="flex items-start gap-2 rounded-md bg-ovation-paper/60 px-3 py-2 text-xs"
                style={{ marginLeft: `${depth * 20}px` }}
              >
                <div
                  className={cn(
                    'mt-0.5 h-2 w-2 shrink-0 rounded-full',
                    depth === 0 ? 'bg-indigo-500' : 'bg-indigo-300',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{entry.ruleName}</div>
                  <div className="text-ovation-muted">
                    {formatActionLabel(entry.action, entry.actionTarget)}
                  </div>
                </div>
                <div className="shrink-0 whitespace-nowrap text-ovation-muted">
                  {relativeTime(entry.matchedAt)}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
